import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
mapboxgl.accessToken = MAPBOX_TOKEN;

// Hexagon Map Component with Surge Pricing Zones
export const HexagonMap = ({ 
  center = [-74.006, 40.7128], 
  zoom = 12,
  hexagons = [],
  markers = [],
  onMapClick,
  showSurgeZones = true,
  className = "h-64"
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: center,
      zoom: zoom
    });

    map.current.on('load', () => {
      setMapLoaded(true);
      
      // Add hexagon source
      map.current.addSource('hexagons', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      // Add hexagon fill layer
      map.current.addLayer({
        id: 'hexagon-fills',
        type: 'fill',
        source: 'hexagons',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.4
        }
      });

      // Add hexagon outline layer
      map.current.addLayer({
        id: 'hexagon-outlines',
        type: 'line',
        source: 'hexagons',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
          'line-opacity': 0.8
        }
      });

      // Add navigation controls
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    });

    map.current.on('click', (e) => {
      if (onMapClick) {
        onMapClick({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update hexagons when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !showSurgeZones) return;

    const features = hexagons.map(hex => ({
      type: 'Feature',
      properties: {
        id: hex.id,
        color: hex.surge?.color || '#22C55E',
        label: hex.surge?.label || 'Normal',
        multiplier: hex.surge?.multiplier || 1.0
      },
      geometry: {
        type: 'Polygon',
        coordinates: [hex.boundary.concat([hex.boundary[0]])]
      }
    }));

    const source = map.current.getSource('hexagons');
    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }
  }, [hexagons, mapLoaded, showSurgeZones]);

  // Update markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove existing markers
    const existingMarkers = document.querySelectorAll('.mapboxgl-marker');
    existingMarkers.forEach(m => m.remove());

    // Add new markers
    markers.forEach(marker => {
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.width = '30px';
      el.style.height = '30px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = marker.color || '#1E4DB7';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      
      if (marker.type === 'driver') {
        el.style.backgroundColor = '#10B981';
        el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" style="width:20px;height:20px;margin:2px"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`;
      } else if (marker.type === 'pickup') {
        el.style.backgroundColor = '#22C55E';
      } else if (marker.type === 'dropoff') {
        el.style.backgroundColor = '#EF4444';
      }

      new mapboxgl.Marker(el)
        .setLngLat([marker.lng, marker.lat])
        .setPopup(marker.popup ? new mapboxgl.Popup().setHTML(marker.popup) : null)
        .addTo(map.current);
    });
  }, [markers, mapLoaded]);

  // Update center
  useEffect(() => {
    if (map.current && mapLoaded) {
      map.current.flyTo({ center, zoom, duration: 1000 });
    }
  }, [center, zoom, mapLoaded]);

  return (
    <div ref={mapContainer} className={`rounded-xl overflow-hidden ${className}`} />
  );
};

// Driver Map Component with full navigation
export const DriverMap = ({
  currentLocation,
  pickup,
  dropoff,
  stops = [],
  hexagons = [],
  nearbyRides = [],
  onNavigate,
  className = "h-96"
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/navigation-night-v1',
      center: currentLocation ? [currentLocation.lng, currentLocation.lat] : [-74.006, 40.7128],
      zoom: 14
    });

    map.current.on('load', () => {
      setMapLoaded(true);

      // Add hexagon layers for surge visualization
      map.current.addSource('surge-zones', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      map.current.addLayer({
        id: 'surge-fills',
        type: 'fill',
        source: 'surge-zones',
        paint: {
          'fill-color': ['get', 'color'],
          'fill-opacity': 0.3
        }
      });

      map.current.addLayer({
        id: 'surge-outlines',
        type: 'line',
        source: 'surge-zones',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 1
        }
      });

      // Add route layer
      map.current.addSource('route', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } }
      });

      map.current.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        paint: {
          'line-color': '#1E4DB7',
          'line-width': 4,
          'line-opacity': 0.8
        }
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      map.current.addControl(new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true
      }));
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update hexagons
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const features = hexagons.map(hex => ({
      type: 'Feature',
      properties: { color: hex.surge?.color || '#22C55E' },
      geometry: {
        type: 'Polygon',
        coordinates: [hex.boundary.concat([hex.boundary[0]])]
      }
    }));

    const source = map.current.getSource('surge-zones');
    if (source) {
      source.setData({ type: 'FeatureCollection', features });
    }
  }, [hexagons, mapLoaded]);

  // Update route when pickup/dropoff changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !pickup || !dropoff) return;

    const coords = [
      [pickup.lng, pickup.lat],
      ...stops.map(s => [s.lng, s.lat]),
      [dropoff.lng, dropoff.lat]
    ];

    const source = map.current.getSource('route');
    if (source) {
      source.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords }
      });
    }

    // Fit bounds to show entire route
    const bounds = coords.reduce((bounds, coord) => {
      return bounds.extend(coord);
    }, new mapboxgl.LngLatBounds(coords[0], coords[0]));

    map.current.fitBounds(bounds, { padding: 50 });
  }, [pickup, dropoff, stops, mapLoaded]);

  return (
    <div className="relative">
      <div ref={mapContainer} className={`${className}`} />
      
      {/* Surge Legend */}
      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur rounded-lg p-3 text-xs shadow-lg">
        <div className="font-semibold mb-2">Demand</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>Surge (2x)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-orange-500" />
            <span>High (1.5x)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-amber-500" />
            <span>Busy (1.25x)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>Normal</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <span>Boost+</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Location Picker with Mapbox
export const MapboxLocationPicker = ({
  label,
  onSelect,
  selectedLocation,
  className = "h-48"
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const [address, setAddress] = useState(selectedLocation?.address || '');

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: selectedLocation ? [selectedLocation.lng, selectedLocation.lat] : [-74.006, 40.7128],
      zoom: 13
    });

    map.current.on('load', () => {
      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    });

    map.current.on('click', async (e) => {
      const { lat, lng } = e.lngLat;
      
      // Update marker
      if (marker.current) {
        marker.current.setLngLat([lng, lat]);
      } else {
        marker.current = new mapboxgl.Marker({ color: '#1E4DB7' })
          .setLngLat([lng, lat])
          .addTo(map.current);
      }

      // Reverse geocode to get address
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
        );
        const data = await response.json();
        const placeName = data.features?.[0]?.place_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        setAddress(placeName);
        onSelect({ lat, lng, address: placeName });
      } catch (error) {
        const addr = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        setAddress(addr);
        onSelect({ lat, lng, address: addr });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      <div ref={mapContainer} className={`rounded-xl overflow-hidden border border-slate-200 ${className}`} />
      {address && (
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <svg className="w-4 h-4 text-[#1E4DB7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate">{address}</span>
        </div>
      )}
    </div>
  );
};

// Navigation Options Component
export const NavigationOptions = ({ destination, origin, onSelect }) => {
  const navOptions = [
    {
      id: 'google',
      name: 'Google Maps',
      icon: '🗺️',
      getUrl: () => {
        const dest = `${destination.lat},${destination.lng}`;
        const orig = origin ? `${origin.lat},${origin.lng}` : '';
        return `https://www.google.com/maps/dir/?api=1&destination=${dest}${orig ? `&origin=${orig}` : ''}`;
      }
    },
    {
      id: 'apple',
      name: 'Apple Maps',
      icon: '🍎',
      getUrl: () => {
        const dest = `${destination.lat},${destination.lng}`;
        return `http://maps.apple.com/?daddr=${dest}`;
      }
    },
    {
      id: 'waze',
      name: 'Waze',
      icon: '🚗',
      getUrl: () => {
        return `https://waze.com/ul?ll=${destination.lat},${destination.lng}&navigate=yes`;
      }
    }
  ];

  return (
    <div className="flex gap-2">
      {navOptions.map(opt => (
        <button
          key={opt.id}
          onClick={() => {
            window.open(opt.getUrl(), '_blank');
            onSelect?.(opt.id);
          }}
          className="flex-1 flex flex-col items-center gap-1 p-3 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
        >
          <span className="text-2xl">{opt.icon}</span>
          <span className="text-xs font-medium">{opt.name}</span>
        </button>
      ))}
    </div>
  );
};

export default HexagonMap;
